import { supabase } from './supabase'
import { base } from './db'
import type {
  CommentaireLocal,
  EpingleSuivi,
  CommentaireSuivi,
  PhotoSuivi,
  Profil,
  Suivi,
  SuiviLocal,
  TypeSuivi
} from './types'

/**
 * Suivis : tâches, déficiences et QRT (phase 3).
 *
 * Même principe que les annotations : tout passe d'abord par la base
 * locale de l'appareil (création, modification, commentaires, photos
 * liées), puis part à la reconnexion avec la synchronisation habituelle
 * (sync.ts). L'identifiant est fixé sur l'appareil : un envoi retenté ne
 * crée jamais de doublon. Le numéro (D-004…) est donné par le serveur.
 */

/* ---------------------------------------------------------------------
   Libellés et cycles de vie
   --------------------------------------------------------------------- */

export const TYPES: { cle: TypeSuivi; libelle: string; singulier: string; prefixe: string }[] = [
  { cle: 'tache', libelle: 'Tâches', singulier: 'tâche', prefixe: 'T' },
  { cle: 'deficience', libelle: 'Déficiences', singulier: 'déficience', prefixe: 'D' },
  { cle: 'qrt', libelle: 'QRT', singulier: 'QRT', prefixe: 'QRT' }
]

export const STATUT_INITIAL: Record<TypeSuivi, string> = {
  tache: 'a_faire',
  deficience: 'ouverte',
  qrt: 'ouverte'
}

export const LIBELLE_STATUT: Record<string, string> = {
  a_faire: 'À faire',
  en_cours: 'En cours',
  terminee: 'Terminée',
  ouverte: 'Ouverte',
  reglee: 'Réglée — à valider',
  validee: 'Validée',
  transmise: 'Transmise',
  repondue: 'Répondue',
  close: 'Close'
}

const FERMES = new Set(['terminee', 'validee', 'close'])

export function estFerme(s: Pick<Suivi, 'statut'>): boolean {
  return FERMES.has(s.statut)
}

/** Date du jour, dans le fuseau local, au format AAAA-MM-JJ. */
export function aujourdhui(): string {
  const d = new Date()
  const n = (x: number) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${n(d.getMonth() + 1)}-${n(d.getDate())}`
}

export function enRetard(s: Suivi): boolean {
  return !!s.echeance && !estFerme(s) && s.echeance < aujourdhui()
}

/** « D-004 », ou « D-… » tant que le serveur n'a pas donné le numéro. */
export function codeSuivi(s: Pick<Suivi, 'type' | 'numero'>): string {
  const prefixe = TYPES.find((t) => t.cle === s.type)?.prefixe ?? '?'
  return `${prefixe}-${s.numero == null ? '…' : String(s.numero).padStart(3, '0')}`
}

/** Classe de pastille pour un statut. */
export function classeStatut(s: Suivi): string {
  if (estFerme(s)) return 'p-ferme'
  if (s.statut === 'reglee' || s.statut === 'transmise') return 'p-attente'
  if (s.statut === 'en_cours' || s.statut === 'repondue') return 'p-neutre'
  return 'p-ouvert'
}

/**
 * Une action de changement de statut proposée sur la fiche.
 * besoin : ce qu'il faut demander avant d'appliquer.
 */
export interface ActionStatut {
  cle: string
  libelle: string
  vers: string
  principale?: boolean
  besoin?: 'motif' | 'transmission' | 'reponse'
}

export function actionsPossibles(s: Suivi, admin: boolean): ActionStatut[] {
  const a: ActionStatut[] = []
  if (s.type === 'tache') {
    if (s.statut === 'a_faire') a.push({ cle: 'commencer', libelle: 'Commencer', vers: 'en_cours' })
    if (s.statut !== 'terminee') a.push({ cle: 'terminer', libelle: 'Marquer terminée', vers: 'terminee', principale: true })
    if (s.statut === 'terminee') a.push({ cle: 'rouvrir', libelle: 'Rouvrir', vers: 'en_cours' })
  } else if (s.type === 'deficience') {
    if (s.statut === 'ouverte') a.push({ cle: 'commencer', libelle: 'Commencer', vers: 'en_cours' })
    if (s.statut === 'ouverte' || s.statut === 'en_cours')
      a.push({ cle: 'regler', libelle: 'Marquer réglée', vers: 'reglee', principale: true })
    if (s.statut === 'reglee' && admin) {
      a.push({ cle: 'valider', libelle: 'Valider', vers: 'validee', principale: true })
      a.push({ cle: 'rejeter', libelle: 'Rejeter', vers: 'en_cours', besoin: 'motif' })
    }
    if (s.statut === 'validee' && admin) a.push({ cle: 'rouvrir', libelle: 'Rouvrir', vers: 'en_cours', besoin: 'motif' })
  } else {
    if (s.statut === 'ouverte')
      a.push({ cle: 'transmettre', libelle: 'Marquer transmise', vers: 'transmise', principale: true, besoin: 'transmission' })
    if (s.statut === 'transmise')
      a.push({ cle: 'repondre', libelle: 'Inscrire la réponse', vers: 'repondue', principale: true, besoin: 'reponse' })
    if (s.statut === 'repondue') {
      a.push({ cle: 'fermer', libelle: 'Fermer la QRT', vers: 'close', principale: true })
      a.push({ cle: 'modifier_reponse', libelle: 'Modifier la réponse', vers: 'repondue', besoin: 'reponse' })
    }
    if (s.statut === 'close') a.push({ cle: 'rouvrir', libelle: 'Rouvrir', vers: 'repondue' })
  }
  return a
}

/** Champs à modifier pour appliquer un changement de statut. */
export function changementsStatut(s: Suivi, vers: string, utilisateurId: string): Partial<Suivi> {
  const maintenant = new Date().toISOString()
  const c: Partial<Suivi> = { statut: vers }
  c.ferme_le = FERMES.has(vers) ? maintenant : null
  if (s.type === 'deficience') {
    if (vers === 'reglee') {
      c.reglee_par = utilisateurId
      c.reglee_le = maintenant
    }
    if (vers === 'validee') {
      c.validee_par = utilisateurId
      c.validee_le = maintenant
    }
    if (vers === 'en_cours' || vers === 'ouverte') {
      c.validee_par = null
      c.validee_le = null
    }
  }
  return c
}

/* ---------------------------------------------------------------------
   Base locale et serveur
   --------------------------------------------------------------------- */

const COLONNES =
  'id, chantier_id, type, numero, titre, description, emplacement, statut, assigne_a, echeance, ' +
  'cree_par, cree_le, modifie_le, ferme_le, reglee_par, reglee_le, validee_par, validee_le, ' +
  'motif_rejet, destinataire, transmise_le, reponse, repondue_le, supprime_le, ' +
  'epingle_document_id, epingle_page, epingle_x, epingle_y, epingle_libelle'

/** Champs qu'on renvoie au serveur lors d'une modification. */
const MODIFIABLES = [
  'titre', 'description', 'emplacement', 'statut', 'assigne_a', 'echeance', 'ferme_le',
  'reglee_par', 'reglee_le', 'validee_par', 'validee_le', 'motif_rejet',
  'destinataire', 'transmise_le', 'reponse', 'repondue_le', 'supprime_le',
  'epingle_document_id', 'epingle_page', 'epingle_x', 'epingle_y', 'epingle_libelle'
] as const

function extraire(s: Suivi): Record<string, unknown> {
  const r: Record<string, unknown> = {}
  for (const k of MODIFIABLES) r[k] = s[k] ?? null
  return r
}

export async function suivisLocaux(chantierId: string): Promise<SuiviLocal[]> {
  const tous = await base.suivis.where('chantier_id').equals(chantierId).toArray()
  return tous.filter((s) => !s.supprime_le)
}

/**
 * Rafraîchit la copie locale d'un chantier (suivis + photos liées), sans
 * toucher à ce qui attend d'être envoyé. Sans réseau : copie locale.
 */
export async function rafraichirSuivis(chantierId: string): Promise<SuiviLocal[]> {
  if (navigator.onLine) {
    const { data, error } = await supabase
      .from('suivis')
      .select(COLONNES)
      .eq('chantier_id', chantierId)
      .is('supprime_le', null)
    if (error) throw error
    const serveur = new Map(((data ?? []) as unknown as Suivi[]).map((s) => [s.id, s]))
    await base.transaction('rw', base.suivis, async () => {
      const locaux = await base.suivis.where('chantier_id').equals(chantierId).toArray()
      for (const l of locaux) if (l.etat === 'ok' && !serveur.has(l.id)) await base.suivis.delete(l.id)
      const enAttente = new Set(locaux.filter((l) => l.etat !== 'ok').map((l) => l.id))
      await base.suivis.bulkPut(
        [...serveur.values()].filter((s) => !enAttente.has(s.id)).map((s) => ({ ...s, etat: 'ok' as const }))
      )
    })

    // Photos liées, par paquets (l'adresse de la requête a une longueur limitée).
    const ids = [...serveur.keys()]
    const liens: { suivi_id: string; photo_client_uuid: string; moment: PhotoSuivi['moment']; ajoute_le: string }[] = []
    for (let i = 0; i < ids.length; i += 80) {
      const { data: d, error: e } = await supabase
        .from('suivi_photos')
        .select('suivi_id, photo_client_uuid, moment, ajoute_le')
        .in('suivi_id', ids.slice(i, i + 80))
      if (e) throw e
      liens.push(...((d ?? []) as typeof liens))
    }
    await base.transaction('rw', base.suivi_photos, async () => {
      const cles = new Set(liens.map((l) => `${l.suivi_id}:${l.photo_client_uuid}`))
      const idsSet = new Set(ids)
      const locaux = await base.suivi_photos.where('suivi_id').anyOf(ids).toArray()
      for (const l of locaux) if (l.etat === 'ok' && idsSet.has(l.suivi_id) && !cles.has(l.cle)) await base.suivi_photos.delete(l.cle)
      const enAttente = new Set(locaux.filter((l) => l.etat !== 'ok').map((l) => l.cle))
      await base.suivi_photos.bulkPut(
        liens
          .map((l) => ({ ...l, cle: `${l.suivi_id}:${l.photo_client_uuid}`, etat: 'ok' as const }))
          .filter((l) => !enAttente.has(l.cle))
      )
    })
  }
  return suivisLocaux(chantierId)
}

/** Suivis épinglés sur une page d'un plan (copie locale, donc aussi hors ligne). */
export async function suivisEpingles(chantierId: string, documentId: string): Promise<SuiviLocal[]> {
  return (await suivisLocaux(chantierId)).filter((s) => s.epingle_document_id === documentId && s.epingle_page != null)
}

export const SANS_EPINGLE: EpingleSuivi = {
  epingle_document_id: null,
  epingle_page: null,
  epingle_x: null,
  epingle_y: null,
  epingle_libelle: null
}

export async function lireSuivi(id: string): Promise<SuiviLocal | undefined> {
  return base.suivis.get(id)
}

/** Nouvelle fiche, enregistrée sur l'appareil ; l'envoi suit. */
export async function creerSuivi(
  champs: Pick<Suivi, 'chantier_id' | 'type' | 'titre' | 'description' | 'emplacement' | 'assigne_a' | 'echeance' | 'destinataire'> &
    EpingleSuivi,
  utilisateurId: string
): Promise<SuiviLocal> {
  const maintenant = new Date().toISOString()
  const s: SuiviLocal = {
    id: crypto.randomUUID(),
    numero: null,
    statut: STATUT_INITIAL[champs.type],
    cree_par: utilisateurId,
    cree_le: maintenant,
    modifie_le: maintenant,
    ferme_le: null,
    reglee_par: null,
    reglee_le: null,
    validee_par: null,
    validee_le: null,
    motif_rejet: null,
    transmise_le: null,
    reponse: null,
    repondue_le: null,
    supprime_le: null,
    ...champs,
    etat: 'a_envoyer',
    essais: 0
  }
  await base.suivis.put(s)
  return s
}

/** Modifie une fiche. Une fiche pas encore envoyée part directement modifiée. */
export async function modifierSuivi(id: string, changements: Partial<Suivi>): Promise<SuiviLocal | undefined> {
  const s = await base.suivis.get(id)
  if (!s) return undefined
  const etat = s.etat === 'a_envoyer' ? 'a_envoyer' : 'a_modifier'
  const nouveau: SuiviLocal = { ...s, ...changements, modifie_le: new Date().toISOString(), etat, essais: 0 }
  await base.suivis.put(nouveau)
  return nouveau
}

/* --- commentaires --------------------------------------------------- */

export async function commentairesLocaux(suiviId: string): Promise<CommentaireLocal[]> {
  return (await base.suivi_commentaires.where('suivi_id').equals(suiviId).toArray()).sort((a, b) =>
    a.cree_le.localeCompare(b.cree_le)
  )
}

export async function rafraichirCommentaires(suiviId: string): Promise<CommentaireLocal[]> {
  if (navigator.onLine) {
    const { data, error } = await supabase
      .from('suivi_commentaires')
      .select('id, suivi_id, auteur_id, texte, evenement, cree_le')
      .eq('suivi_id', suiviId)
    if (!error && data) {
      const locaux = await base.suivi_commentaires.where('suivi_id').equals(suiviId).toArray()
      const enAttente = new Set(locaux.filter((l) => l.etat !== 'ok').map((l) => l.id))
      await base.suivi_commentaires.bulkPut(
        (data as CommentaireSuivi[]).filter((c) => !enAttente.has(c.id)).map((c) => ({ ...c, etat: 'ok' as const }))
      )
    }
  }
  return commentairesLocaux(suiviId)
}

export async function ajouterCommentaire(
  suiviId: string,
  auteurId: string,
  texte: string,
  evenement: string | null = null
): Promise<void> {
  await base.suivi_commentaires.put({
    id: crypto.randomUUID(),
    suivi_id: suiviId,
    auteur_id: auteurId,
    texte,
    evenement,
    cree_le: new Date().toISOString(),
    etat: 'a_envoyer',
    essais: 0
  })
}

/* --- photos liées --------------------------------------------------- */

export async function photosDuSuivi(suiviId: string): Promise<PhotoSuivi[]> {
  return (await base.suivi_photos.where('suivi_id').equals(suiviId).toArray())
    .filter((p) => p.etat !== 'a_retirer')
    .sort((a, b) => a.ajoute_le.localeCompare(b.ajoute_le))
}

/** Nombre de photos par suivi, pour la liste. */
export async function comptePhotos(suiviIds: string[]): Promise<Map<string, number>> {
  const m = new Map<string, number>()
  if (suiviIds.length === 0) return m
  for (const p of await base.suivi_photos.where('suivi_id').anyOf(suiviIds).toArray()) {
    if (p.etat === 'a_retirer') continue
    m.set(p.suivi_id, (m.get(p.suivi_id) ?? 0) + 1)
  }
  return m
}

export async function lierPhoto(suiviId: string, photoClientUuid: string, moment: PhotoSuivi['moment'] = 'reference'): Promise<void> {
  const cle = `${suiviId}:${photoClientUuid}`
  const existant = await base.suivi_photos.get(cle)
  if (existant && existant.etat !== 'a_retirer') return
  await base.suivi_photos.put({
    cle,
    suivi_id: suiviId,
    photo_client_uuid: photoClientUuid,
    moment,
    ajoute_le: new Date().toISOString(),
    etat: existant?.etat === 'a_retirer' ? 'ok' : 'a_envoyer',
    essais: 0
  })
}

export async function retirerPhoto(suiviId: string, photoClientUuid: string): Promise<void> {
  const cle = `${suiviId}:${photoClientUuid}`
  const p = await base.suivi_photos.get(cle)
  if (!p) return
  if (p.etat === 'a_envoyer') await base.suivi_photos.delete(cle)
  else await base.suivi_photos.put({ ...p, etat: 'a_retirer', essais: 0 })
}

/* ---------------------------------------------------------------------
   Envoi (appelé par sync.ts)
   --------------------------------------------------------------------- */

export async function suivisEnAttente(): Promise<number> {
  return (
    (await base.suivis.where('etat').anyOf('a_envoyer', 'a_modifier').count()) +
    (await base.suivi_commentaires.where('etat').equals('a_envoyer').count()) +
    (await base.suivi_photos.where('etat').anyOf('a_envoyer', 'a_retirer').count())
  )
}

function message(e: unknown): string {
  if (e instanceof Error) return e.message
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: unknown }).message)
  return String(e)
}

/**
 * Envoie les fiches, puis les commentaires et les photos liées.
 * Chaque catégorie s'arrête au premier échec (connexion faible).
 * Un commentaire ou une photo attend que sa fiche soit passée.
 */
export async function envoyerSuivis(utilisateurId: string, apresChaque?: () => Promise<void>): Promise<void> {
  // 1. Fiches
  const fiches = await base.suivis.where('etat').anyOf('a_envoyer', 'a_modifier').sortBy('cree_le')
  for (const s of fiches) {
    try {
      if (s.etat === 'a_envoyer') {
        const { data, error } = await supabase
          .from('suivis')
          .insert({
            id: s.id,
            chantier_id: s.chantier_id,
            type: s.type,
            cree_par: utilisateurId,
            cree_le: s.cree_le,
            ...extraire(s)
          })
          .select('numero')
          .single()
        let numero = (data as { numero: number } | null)?.numero ?? null
        if (error) {
          // 23505 = déjà passée lors d'un envoi précédent : on relit son numéro.
          if (error.code !== '23505') throw error
          const { data: d } = await supabase.from('suivis').select('numero').eq('id', s.id).single()
          numero = (d as { numero: number } | null)?.numero ?? null
        }
        const maintenant = await base.suivis.get(s.id)
        if (maintenant) {
          // Modifiée pendant l'envoi : la modification partira au prochain tour.
          const inchange = JSON.stringify(extraire(maintenant)) === JSON.stringify(extraire(s))
          await base.suivis.put({
            ...maintenant,
            numero,
            etat: inchange ? 'ok' : 'a_modifier',
            derniere_erreur: undefined
          })
        }
      } else {
        const champs = extraire(s)
        const { error } = await supabase
          .from('suivis')
          .update(s.supprime_le ? { ...champs, supprime_par: utilisateurId } : champs)
          .eq('id', s.id)
        if (error) throw error
        const maintenant = await base.suivis.get(s.id)
        if (maintenant && maintenant.etat === 'a_modifier' && JSON.stringify(extraire(maintenant)) === JSON.stringify(champs)) {
          await base.suivis.update(s.id, { etat: 'ok', derniere_erreur: undefined })
        }
      }
      await apresChaque?.()
    } catch (e) {
      await base.suivis.update(s.id, { essais: (s.essais ?? 0) + 1, derniere_erreur: message(e) })
      break
    }
  }

  const ficheEnvoyee = async (id: string) => (await base.suivis.get(id))?.etat !== 'a_envoyer'

  // 2. Commentaires
  const commentaires = await base.suivi_commentaires.where('etat').equals('a_envoyer').sortBy('cree_le')
  for (const c of commentaires) {
    if (!(await ficheEnvoyee(c.suivi_id))) continue
    try {
      const { error } = await supabase.from('suivi_commentaires').insert({
        id: c.id,
        suivi_id: c.suivi_id,
        auteur_id: utilisateurId,
        texte: c.texte,
        evenement: c.evenement,
        cree_le: c.cree_le
      })
      if (error && error.code !== '23505') throw error
      await base.suivi_commentaires.update(c.id, { etat: 'ok', derniere_erreur: undefined })
      await apresChaque?.()
    } catch (e) {
      await base.suivi_commentaires.update(c.id, { essais: (c.essais ?? 0) + 1, derniere_erreur: message(e) })
      break
    }
  }

  // 3. Photos liées
  const photos = await base.suivi_photos.where('etat').anyOf('a_envoyer', 'a_retirer').sortBy('ajoute_le')
  for (const p of photos) {
    if (!(await ficheEnvoyee(p.suivi_id))) continue
    try {
      if (p.etat === 'a_envoyer') {
        const { error } = await supabase.from('suivi_photos').insert({
          suivi_id: p.suivi_id,
          photo_client_uuid: p.photo_client_uuid,
          moment: p.moment,
          ajoute_par: utilisateurId,
          ajoute_le: p.ajoute_le
        })
        if (error && error.code !== '23505') throw error
        await base.suivi_photos.update(p.cle, { etat: 'ok', derniere_erreur: undefined })
      } else {
        const { error } = await supabase
          .from('suivi_photos')
          .delete()
          .eq('suivi_id', p.suivi_id)
          .eq('photo_client_uuid', p.photo_client_uuid)
        if (error) throw error
        await base.suivi_photos.delete(p.cle)
      }
      await apresChaque?.()
    } catch (e) {
      await base.suivi_photos.update(p.cle, { essais: (p.essais ?? 0) + 1, derniere_erreur: message(e) })
      break
    }
  }
}

/* ---------------------------------------------------------------------
   Équipe (pour l'assignation et l'affichage des noms)
   --------------------------------------------------------------------- */

const CLE_EQUIPE = 'chantiers-larente:equipe'

/** Profils actifs, gardés sur l'appareil pour l'usage hors ligne. */
export async function equipe(): Promise<Pick<Profil, 'id' | 'nom' | 'role'>[]> {
  if (navigator.onLine) {
    const { data } = await supabase.from('profils').select('id, nom, role, actif').eq('actif', true).order('nom')
    if (data) {
      const liste = (data as Profil[]).map(({ id, nom, role }) => ({ id, nom, role }))
      try {
        localStorage.setItem(CLE_EQUIPE, JSON.stringify(liste))
      } catch {
        /* stockage indisponible : sans conséquence */
      }
      return liste
    }
  }
  try {
    return JSON.parse(localStorage.getItem(CLE_EQUIPE) ?? '[]')
  } catch {
    return []
  }
}
